import { Commend3Model } from "./Types";
import {
  getLinkedStrategyGeneration,
  getPrimarySubModel,
  getStrategyLabel,
  getUpdatedByName,
  hasStrategy,
  isMappedToWidget,
  isPromoted,
  isTransient,
} from "./Commend3Helper";
import {
  FILTER_FACETS,
  GENERATION_STATUS,
  NOT_CONFIGURED_LABEL,
  NOT_MAPPED_LABEL,
} from "./Commend3Constants";

export type Commend3FacetPicks = Record<string, string[]>;

export const promotionStateOf = (model: Commend3Model): string => {
  if (isTransient(model)) return "promoting";
  if (isPromoted(model)) return isMappedToWidget(model) ? "live" : "promoted";
  return "not-promoted";
};

export const generationLabelOf = (model: Commend3Model): string | null => {
  if (!hasStrategy(model)) return NOT_CONFIGURED_LABEL;
  const generation = getLinkedStrategyGeneration(model);
  if (!generation) return null;
  if (generation.status === GENERATION_STATUS.GENERATING) return "Generating";
  if (generation.status === GENERATION_STATUS.GENERATED) return "Generated";
  return "Not generated";
};

export const widgetValueOf = (model: Commend3Model): string =>
  model?.widget?.name?.trim() || NOT_MAPPED_LABEL;

export const FACET_VALUE_RESOLVERS: Record<
  string,
  (model: Commend3Model) => string | null
> = {
  promotion: promotionStateOf,
  generation: generationLabelOf,
  type: (model) => model.type || null,
  strategy: (model) =>
    hasStrategy(model) ? getStrategyLabel(model) : NOT_CONFIGURED_LABEL,
  widget: widgetValueOf,
};

export const countPicks = (picks: Commend3FacetPicks): number =>
  Object.values(picks).reduce((total, list) => total + list.length, 0);

export const applyFacetPicks = (
  models: Commend3Model[],
  picks: Commend3FacetPicks
): Commend3Model[] =>
  models.filter((model) =>
    FILTER_FACETS.every((facet) => {
      const selected = picks[facet.key] ?? [];
      if (!selected.length) return true;
      const resolver = FACET_VALUE_RESOLVERS[facet.key];
      if (!resolver) return true;
      const value = resolver(model);
      return value !== null && selected.includes(value);
    })
  );

export const matchesSearch = (model: Commend3Model, term: string): boolean => {
  if (!term) return true;
  const needle = term.toLowerCase();
  return (
    (model.name ?? "").toLowerCase().includes(needle) ||
    getStrategyLabel(model).toLowerCase().includes(needle) ||
    getUpdatedByName(model).toLowerCase().includes(needle)
  );
};

export type Commend3ClientSortField =
  | "name"
  | "strategy"
  | "generation"
  | "promotion"
  | "widget"
  | "updated"
  | "by";

const sortValueOf = (model: Commend3Model, field: Commend3ClientSortField): string => {
  if (field === "promotion") return promotionStateOf(model);
  if (field === "generation") return generationLabelOf(model) ?? "";
  if (field === "strategy") return getPrimarySubModel(model)?.strategy_name ?? "";
  if (field === "widget") return model?.widget?.name ?? "";
  if (field === "by") return getUpdatedByName(model);
  if (field === "updated") return String(model.updated_at ?? "");
  return model.name ?? "";
};

export const sortModels = (
  models: Commend3Model[],
  field: Commend3ClientSortField,
  order: "asc" | "desc"
): Commend3Model[] => {
  const direction = order === "desc" ? -1 : 1;
  return [...models].sort(
    (a, b) =>
      String(sortValueOf(a, field)).localeCompare(
        String(sortValueOf(b, field)),
        undefined,
        { numeric: true }
      ) * direction
  );
};
